import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { hubAktif, tataLetakBawah, type HubKey, type NavHub } from '../lib/nav-hub';

/**
 * Ikon navigasi — SVG sebaris, bukan pustaka ikon dan bukan emoji.
 *
 * Aplikasi lama memakai `lucide-react`. Untuk lima ikon, itu satu dependensi penuh demi beberapa
 * ratus byte jalur SVG — dan aplikasi ini dipakai kurir bersinyal buruk, tempat setiap kilobyte
 * bundel dibayar setiap kali cache kosong.
 *
 * Emoji ditolak karena bentuknya berubah antar-perangkat dan ia dibacakan pembaca layar sebagai
 * kalimat ("wajah tersenyum"), bukan sebagai label menu.
 */
const PATHS: Readonly<Record<HubKey | 'lainnya', string>> = {
  beranda: 'M3 11l9-8 9 8v9a2 2 0 0 1-2 2h-4v-6H9v6H5a2 2 0 0 1-2-2z',
  laporan: 'M18 20V10M12 20V4M6 20V14',
  absen: 'M12 7v5l3 2M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18z',
  absensi: 'M3 12a9 9 0 1 0 3-6.7M3 4v4h4M12 8v4l3 2',
  tugas: 'M9 5h6M9 5a2 2 0 0 0-2 2v0h10v0a2 2 0 0 0-2-2M5 7h14v13H5zM9 12h6M9 16h4',
  karyawan:
    'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  alat: 'M4 5h16v11H4zM8 20h8M12 16v4',
  pengaturan:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-2.9-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 3 15H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.2-2.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 9 4.6V4a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0 1.2 2.9H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
  lainnya: 'M4 6h16M4 12h16M4 18h16',
};

const FALLBACK = 'M5 12h14';

function Icon({ ikon }: { readonly ikon: HubKey | 'lainnya' }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      // Ikon di sini DEKORATIF: setiap tombol sudah membawa labelnya sebagai teks di bawahnya.
      // Memberi ikon nama sendiri akan membuat pembaca layar mengucapkan label dua kali.
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[ikon] ?? FALLBACK} />
    </svg>
  );
}

/**
 * Navigasi bawah — diadaptasi dari aplikasi absensi-kurir Emguard.
 *
 * Alasannya bukan gaya: layar ini dipakai satu tangan sambil berdiri, dan ibu jari mencapai tepi
 * bawah jauh lebih mudah daripada tepi atas. Bilah pil yang menggulir mendatar di atas menuntut
 * dua gerakan — menggulir untuk menemukan, lalu menekan.
 *
 * LABEL TETAP ADA di bawah tiap ikon. Ikon tanpa label menuntut orang menghafal arti bentuk, dan
 * di aplikasi lapangan yang dipakai sesekali, hafalan itu tidak pernah terbentuk.
 */
export function BottomNav({ hubs }: { readonly hubs: readonly NavHub[] }): ReactNode {
  const { primary, tombolLainnya } = tataLetakBawah(hubs);
  const { pathname } = useLocation();
  // Dihitung atas SEMUA grup, bukan hanya `primary`: berada di layar grup yang terselip di
  // Lainnya (mis. Pengaturan) menyalakan "Lainnya", bukan menyalakan tombol yang salah.
  const aktif = hubAktif(hubs, pathname);
  const lainnyaAktif =
    pathname === '/menu' || (aktif !== null && !primary.some((h) => h.key === aktif));

  return (
    <nav className="bottom-nav" aria-label="Navigasi utama">
      {primary.map((hub) => (
        <Link
          key={hub.key}
          to={hub.to}
          className={hub.key === aktif ? 'bottom-nav-item active' : 'bottom-nav-item'}
          aria-current={hub.key === aktif ? 'page' : undefined}
        >
          <Icon ikon={hub.key} />
          <span>{hub.label}</span>
        </Link>
      ))}
      {/* Rumah tombol Keluar — kapan ia tampil diputuskan `tataLetakBawah`, bukan di sini. */}
      {tombolLainnya ? (
        <Link
          to="/menu"
          className={lainnyaAktif ? 'bottom-nav-item active' : 'bottom-nav-item'}
          aria-current={lainnyaAktif ? 'page' : undefined}
        >
          <Icon ikon="lainnya" />
          <span>Lainnya</span>
        </Link>
      ) : null}
    </nav>
  );
}
